import { useTranslation } from "react-i18next";

import { TagSelectorPopover } from "@/components/Tags/TagAssignmentSheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  ScheduleResourceFormState,
  ScheduleResourceSelector,
} from "@/components/Schedule/ResourceSelector";
import RadioInput from "@/components/ui/RadioInput";
import { TagConfig, TagResource } from "@/types/emr/tagConfig/tagConfig";
import {
  APPOINTMENT_VISIT_TYPE_LABELS,
  AppointmentVisitType,
  PATIENT_VISIT_TYPE_LABELS,
  PatientVisitType,
  SchedulableResourceType,
} from "@/types/scheduling/schedule";

interface AppointmentFormSectionProps {
  facilityId: string;
  selectedTags: TagConfig[];
  setSelectedTags: (tags: TagConfig[]) => void;
  reason: string;
  setReason: (reason: string) => void;
  selectedVisitType?: AppointmentVisitType;
  setSelectedVisitType?: (visitType: AppointmentVisitType) => void;
  selectedPatientVisitType?: PatientVisitType;
  setSelectedPatientVisitType?: (visitType: PatientVisitType) => void;
  selectedResource: ScheduleResourceFormState;
  setSelectedResource: (resource: ScheduleResourceFormState) => void;
}
export const AppointmentFormSection = ({
  facilityId,
  selectedTags,
  setSelectedTags,
  reason,
  setReason,
  selectedVisitType,
  setSelectedVisitType,
  selectedPatientVisitType,
  setSelectedPatientVisitType,
  selectedResource,
  setSelectedResource,
}: AppointmentFormSectionProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex flex-col">
        <Label className="mb-2 text-sm font-medium text-gray-950 dark:text-gray-50">
          {t("select_resource_type")}
        </Label>
        <RadioInput
          options={Object.values(SchedulableResourceType).map((type) => ({
            label: t(`resource_type__${type}`),
            value: type,
          }))}
          value={selectedResource.resource_type}
          onValueChange={(value: SchedulableResourceType) => {
            if (selectedResource.resource_type === value) {
              return;
            }
            setSelectedResource({
              resource: null,
              resource_type: value,
            });
          }}
          required={true}
        />
      </div>
      {selectedVisitType !== undefined && setSelectedVisitType && (
        <div className="flex flex-col">
          <Label className="mb-2 text-sm font-medium text-gray-950 dark:text-gray-50">
            {t("visit_type")}
          </Label>
          <RadioInput
            options={Object.values(AppointmentVisitType).map((type) => ({
              label: t(`visit_type__${APPOINTMENT_VISIT_TYPE_LABELS[type]}`),
              value: type,
            }))}
            value={selectedVisitType}
            onValueChange={(value: AppointmentVisitType) =>
              setSelectedVisitType(value)
            }
            required={true}
          />
        </div>
      )}
      {selectedPatientVisitType !== undefined &&
        setSelectedPatientVisitType && (
          <div className="flex flex-col">
            <Label className="mb-2 text-sm font-medium text-gray-950 dark:text-gray-50">
              {t("patient_visit_type")}
            </Label>
            <RadioInput
              options={Object.values(PatientVisitType).map((type) => ({
                label: t(`patient_visit_type__${PATIENT_VISIT_TYPE_LABELS[type]}`),
                value: type,
              }))}
              value={selectedPatientVisitType}
              onValueChange={(value: PatientVisitType) =>
                setSelectedPatientVisitType(value)
              }
              required={true}
            />
          </div>
        )}
      <div className="flex flex-col">
        <Label className="mb-2 text-sm font-medium text-gray-950 dark:text-gray-50">
          {t(`schedulable_resource__${selectedResource.resource_type}`)}
        </Label>
        <ScheduleResourceSelector
          facilityId={facilityId}
          setSelectedResource={setSelectedResource}
          selectedResource={selectedResource}
        />
      </div>

      <div className="max-w-md">
        <Label className="mb-2">{t("tags", { count: 1 })}</Label>
        <TagSelectorPopover
          selected={selectedTags}
          onChange={setSelectedTags}
          resource={TagResource.APPOINTMENT}
          facilityId={facilityId}
        />
      </div>
      <div className="w-full">
        <Label className="mb-2 text-sm font-medium text-gray-950 dark:text-gray-50">
          {t("reason_for_visit_label")}
          <span className="font-normal italic">({t("optional")})</span>
        </Label>
        <Textarea
          placeholder={t("reason_for_visit")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="min-h-10 px-3 py-2"
        />
      </div>
    </>
  );
};
